import React from 'react';

import { EditableSection } from '@/components/EditableSection';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';

import { SettingCompactCard } from './primitives/SettingCompactCard';

type ModalSize =
  | 'xs'
  | 'lg'
  | 'xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'full';

/**
 * Type-safe props for SettingEditableCard with generic form props support
 *
 * This component accepts any form component with flexible props by using
 * a generic type parameter. This approach:
 * - Maintains type safety at usage sites
 * - Allows each form to define its own prop interface
 * - Delegates type checking to EditableSection (which also uses generics)
 * - Prevents artificial coupling between form components
 *
 * Different forms have different requirements:
 * - ScanLevelSetupForm: { button?, customerEmail? }
 * - SourceIPForm: { customerEmail }
 * - RateLimitingForm: { ... }
 *
 * @example
 * // Usage with ScanLevelSetupForm
 * <SettingEditableCard
 *   editConfig={{
 *     modalTitle: 'Scan Levels',
 *     formComponent: ScanLevelSetupForm,
 *     formProps: { customerEmail: 'user@example.com' },
 *   }}
 * >
 *   <div>Content</div>
 * </SettingEditableCard>
 *
 * @example
 * // Usage with different form
 * <SettingEditableCard
 *   editConfig={{
 *     modalTitle: 'Source IP',
 *     formComponent: SourceIPForm,
 *     formProps: { customerEmail: email },
 *   }}
 * >
 *   <div>Content</div>
 * </SettingEditableCard>
 */

export interface SettingEditableCardProps<
  TFormProps = Record<string, unknown>,
> {
  /** Card content (labels, values, nested components) or render function with modal control */
  children:
    | React.ReactNode
    | ((modalControl: { openModal: () => void }) => React.ReactNode);
  /** Edit configuration - if provided, shows Edit button with modal */
  editConfig: {
    modalTitle: string;
    /** Form component - can be any component with any props */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formComponent: React.ComponentType<any>;
    /** Props to pass to form component - type checked at usage site */
    formProps?: TFormProps;
    modalSize?: ModalSize;
    modalClassName?: string;
    hideFooter?: boolean;
    strategy?: 'url-state' | 'local-state';
    urlParam?: string;
    footer?: {
      text?: string;
      form?: string;
      hideCancelButton?: boolean;
    };
  };
  /** Whether edit functionality is enabled (default: true) */
  editable?: boolean;
  /**
   * When `editable` is false and this is set, render the Edit button DISABLED
   * with this tooltip on hover instead of hiding it (read-only lock
   * affordance). Used for customer-visible/Praetorian-managed cards.
   */
  lockedTooltip?: string;
  /** Test ID for E2E testing */
  'data-testid'?: string;
  'data-tutorial'?: string;
  /** Additional className for Container */
  className?: string;
}

/**
 * Editable card with Edit button and modal functionality.
 *
 * Combines SettingCompactCard with EditableSection to provide:
 * - Content on left (flex-col wrapper)
 * - Edit button on right (absolute positioned)
 * - Modal with form component
 * - Automatic modal state management
 *
 * @remarks
 * This component enforces the established pattern:
 * - SettingCompactCard base with items-center justify-between px-4 py-2
 * - Content on left (flex flex-col gap-2)
 * - Edit button on right (if editable)
 * - EditableSection wrapper for modal functionality
 *
 * Pattern Rules:
 * - items-center: Vertically centers Edit button with content
 * - py-2: Consistent 8px vertical padding
 * - min-h-24: Minimum 96px height for visual rhythm
 * - gap-2: 8px spacing between content rows
 *
 * @example
 * ```tsx
 * // Simple editable card
 * <SettingEditableCard
 *   editConfig={{
 *     modalTitle: 'Source IP Configuration',
 *     formComponent: SourceIPForm,
 *     formProps: { customerEmail: email },
 *     modalSize: '2xl',
 *   }}
 * >
 *   <div className="flex flex-col gap-2">
 *     <div className="flex items-center gap-2">
 *       <h4 className="font-bold">Source IP:</h4>
 *       <span>Static (66.45.78.0/24)</span>
 *     </div>
 *   </div>
 * </SettingEditableCard>
 *
 * // Read-only display (editable=false)
 * <SettingEditableCard
 *   editable={false}
 *   editConfig={{ ... }}
 * >
 *   <div>Content</div>
 * </SettingEditableCard>
 * ```
 */
/**
 * Implementation note: Uses 'any' for maximum flexibility
 *
 * Form components in this codebase have vastly different prop signatures:
 * - ScanLevelSetupForm manages its own modal state via URL params
 * - SourceIPForm expects simple customerEmail prop
 * - Other forms have completely different requirements
 *
 * Using 'any' here delegates type checking to EditableSection, which
 * wraps forms and handles their lifecycle. Type safety is maintained
 * at the usage site where the specific form component is referenced.
 */
export const SettingEditableCard = <TFormProps = Record<string, unknown>,>({
  children,
  editConfig,
  editable = true,
  lockedTooltip,
  'data-testid': testId,
  'data-tutorial': tutorial,
  className,
}: SettingEditableCardProps<TFormProps>) => {
  return (
    <SettingCompactCard
      className={className}
      data-testid={testId}
      data-tutorial={tutorial}
    >
      {editable ? (
        <EditableSection
          config={
            editConfig.strategy === 'url-state'
              ? {
                  strategy: 'url-state',
                  urlParam: editConfig.urlParam || 'edit',
                  modalTitle: editConfig.modalTitle,
                  modal: {
                    size: editConfig.modalSize || '4xl',
                    className: editConfig.modalClassName || 'px-8 py-6',
                    hideFooter: editConfig.hideFooter,
                    ...(editConfig.footer && { footer: editConfig.footer }),
                  },
                }
              : {
                  strategy: 'local-state',
                  modalTitle: editConfig.modalTitle,
                  modal: {
                    size: editConfig.modalSize || '4xl',
                    className: editConfig.modalClassName || 'px-8 py-6',
                    hideFooter: editConfig.hideFooter,
                    ...(editConfig.footer && { footer: editConfig.footer }),
                  },
                }
          }
          formComponent={editConfig.formComponent}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formProps={editConfig.formProps as any}
          className="w-full min-w-0"
        >
          {({ openModal }) => (
            <>
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {typeof children === 'function'
                  ? children({ openModal })
                  : children}
              </div>
              <div className="absolute top-1/2 right-4 -translate-y-1/2">
                <Button onClick={openModal} variant="ghost" size="sm">
                  Edit
                </Button>
              </div>
            </>
          )}
        </EditableSection>
      ) : (
        <>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {typeof children === 'function'
              ? children({ openModal: () => {} })
              : children}
          </div>
          {lockedTooltip && (
            <div className="absolute top-1/2 right-4 -translate-y-1/2">
              <Tooltip title={lockedTooltip}>
                {/* span wrapper enables hover/focus events on disabled button for tooltip */}
                <span tabIndex={0} data-testid="edit-locked">
                  <Button variant="ghost" size="sm" disabled>
                    Edit
                  </Button>
                </span>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </SettingCompactCard>
  );
};
